import React from 'react';

import { Box, Typography } from '@mui/material';
import { FormSpy } from 'react-final-form';
import { useNavigate } from 'react-router';

import { useAuthCredentials } from '@hrlogics/react-auth';
import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { appRoutes } from '@clearverify/constants';

import { Button, FullscreenLoader } from 'src/components/common';
import { PasswordField, TextField } from 'src/components/finalForm';
import { useCognito } from 'src/hooks/app';
import { useCurrentUser } from 'src/hooks/user';

import { SignInFormField, SignInFormValues } from '../types';
import { signInFormValidationSchema } from '../utils/validation';

import { MfaSetupNote } from './MfaSetupNote';

type MfaLoginFormProps = {
  handleLoginAction?: () => void;
  isAuthRoute?: boolean;
  isMfaRequired?: boolean;
};

export const MfaLoginForm: React.FC<MfaLoginFormProps> = ({
  handleLoginAction,
  isAuthRoute,
  isMfaRequired,
}) => {
  const navigate = useNavigate();

  const { setAuthCredentials } = useAuthCredentials();
  const { user, isUserLoading } = useCurrentUser();

  const initialValues: Partial<SignInFormValues> = {
    email: user?.email || '',
  };

  const { signIn } = useCognito();

  const handleFormSubmit = React.useCallback(
    async (formValues: SignInFormValues) => {
      try {
        const signInData = await signIn({
          username: formValues.email,
          password: formValues.password,
        });

        if (signInData.accessToken) {
          handleLoginAction?.();
          setAuthCredentials(null, signInData.accessToken);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [handleLoginAction, setAuthCredentials, signIn],
  );

  const handleLogout = React.useCallback(async () => {
    navigate(appRoutes.auth.logout);
  }, [navigate]);

  const handleLaterSetup = React.useCallback(() => {
    navigate(appRoutes.root);
  }, [navigate]);

  if (isUserLoading) {
    return <FullscreenLoader />;
  }

  return (
    <Form<SignInFormValues>
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      subscription={{ submitting: true }}
      validate={validateWithYupSchema(signInFormValidationSchema)}
    >
      {({ submitting }) => (
        <React.Fragment>
          <MfaSetupNote isAuthStep isAuthRoute={isAuthRoute} isMfaRequired={isMfaRequired} />

          <Box mt={4}>
            <Field
              name={SignInFormField.email}
              component={TextField}
              label="Email"
              fullWidth
              required
              disabled
              slotProps={{
                input: {
                  autoComplete: 'email',
                },
              }}
            />
          </Box>

          <Box mt={2}>
            <Field
              name={SignInFormField.password}
              component={PasswordField}
              label="Password"
              fullWidth
              required
              disabled={submitting}
              slotProps={{
                input: {
                  autoComplete: 'current-password',
                },
              }}
            />
          </Box>

          <Box mt={3}>
            <Typography color="gray">
              Once you have downloaded an Authenticator App, click the button below to generate your
              QR code.
            </Typography>
          </Box>

          <Box display="flex" mt={4} gap={2} justifyContent="center">
            {isAuthRoute && (
              <React.Fragment>
                {isMfaRequired ? (
                  <Button
                    type="button"
                    color="error"
                    variant="outlined"
                    onClick={handleLogout}
                    disabled={submitting}
                    loading={submitting}
                  >
                    Sign Out
                  </Button>
                ) : (
                  <Button
                    type="button"
                    color="primary"
                    variant="outlined"
                    onClick={handleLaterSetup}
                    disabled={submitting}
                    loading={submitting}
                  >
                    Later
                  </Button>
                )}
              </React.Fragment>
            )}

            <FormSpy subscription={{ valid: true }}>
              {({ valid }) => (
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={submitting || !valid}
                  loading={submitting}
                >
                  Generate QR code
                </Button>
              )}
            </FormSpy>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};
